import type {
    RoleName,
    Permission,
    Resource,
    Action,
    RouteProtection,
    RBACUser,
    PermissionChecker
} from '../types/rbac';
import { ROLE_HIERARCHY, DEFAULT_ROLE_PERMISSIONS } from '../types/rbac';

/**
 * Action implication rules. For example, if a route requires 'view',
 * having 'update', 'delete', or 'create' should implicitly satisfy it.
 */
function actionImplies(have: Action, required: Action): boolean {
    // Having any of mutate permissions implies ability to view
    if (required === 'view') {
        return have === 'view' || have === 'update' || have === 'delete' || have === 'create';
    }
    // By default, no other implications
    return false;
}

/**
 * Normalize resource/action names and accept common aliases.
 */
function normalizeResource(resource: string): Resource | null {
    const r = resource.toLowerCase();
    if (r === 'project' || r === 'projects') return 'project';
    if (r === 'user' || r === 'users') return 'user';
    return null;
}

function normalizeAction(action: string): Action | null {
    const a = action.toLowerCase();
    if (a === 'view' || a === 'read' || a === 'show') return 'view';
    if (a === 'create' || a === 'add' || a === 'new') return 'create';
    if (a === 'update' || a === 'edit' || a === 'modify') return 'update';
    if (a === 'delete' || a === 'remove' || a === 'destroy') return 'delete';
    if (a === 'list') return 'list' as Action; // list is only valid for 'user' in our model
    if (a === 'set-role' || a === 'set_roles' || a === 'assign-role') return 'set-role' as Action;
    if (a === 'ban') return 'ban' as Action;
    if (a === 'impersonate') return 'impersonate' as Action;
    if (a === 'set-password' || a === 'set_password' || a === 'reset-password') return 'set-password' as Action;
    return null;
}

function normalizePermissionLoose(p: any): Permission | null {
    try {
        if (!p) return null;
        if (typeof p === 'string') {
            const [resRaw, actRaw] = p.split(':');
            if (!resRaw || !actRaw) return null;
            const resource = normalizeResource(resRaw);
            const action = normalizeAction(actRaw);
            if (!resource || !action) return null;
            return { resource, action } as Permission;
        }
        if (typeof p === 'object') {
            const resource = normalizeResource(String(p.resource ?? ''));
            const action = normalizeAction(String(p.action ?? ''));
            if (!resource || !action) return null;
            return { resource, action } as Permission;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Check if a user has a specific permission
 */
export function hasPermission(
    userPermissions: Permission[],
    resource: Resource,
    action: Action
): boolean {
    return userPermissions.some(permission =>
        permission.resource === resource && (
            permission.action === action || actionImplies(permission.action as Action, action)
        )
    );
}

/**
 * Check if a user has a specific role
 */
export function hasRole(userRoles: RoleName[], role: RoleName): boolean {
    return userRoles.includes(role);
}

/**
 * Check if a user has any of the specified roles
 */
export function hasAnyRole(userRoles: RoleName[], roles: RoleName[]): boolean {
    return roles.some(role => userRoles.includes(role));
}

/**
 * Check if a user has all of the specified roles
 */
export function hasAllRoles(userRoles: RoleName[], roles: RoleName[]): boolean {
    return roles.every(role => userRoles.includes(role));
}

/**
 * Check if a user has any of the specified permissions
 */
export function hasAnyPermission(
    userPermissions: Permission[],
    permissions: Permission[]
): boolean {
    return permissions.some(permission =>
        hasPermission(userPermissions, permission.resource, permission.action)
    );
}

/**
 * Check if a user has all of the specified permissions
 */
export function hasAllPermissions(
    userPermissions: Permission[],
    permissions: Permission[]
): boolean {
    return permissions.every(permission =>
        hasPermission(userPermissions, permission.resource, permission.action)
    );
}

/**
 * Get permissions for a role from default configuration
 */
export function getPermissionsForRole(role: RoleName): Permission[] {
    return DEFAULT_ROLE_PERMISSIONS[role] || [];
}

/**
 * Get all permissions for multiple roles
 */
export function getPermissionsForRoles(roles: RoleName[]): Permission[] {
    const allPermissions: Permission[] = [];
    const seen = new Set<string>();

    roles.forEach(role => {
        const rolePermissions = getPermissionsForRole(role);
        rolePermissions.forEach(permission => {
            const key = `${permission.resource}:${permission.action}`;
            if (!seen.has(key)) {
                seen.add(key);
                allPermissions.push(permission);
            }
        });
    });

    return allPermissions;
}

/**
 * Check if a user can access a route based on protection configuration
 */
export function canAccessRoute(
    user: RBACUser | null,
    protection: RouteProtection
): boolean {
    // If no protection is configured, allow access
    if (!protection.requireAuth && !protection.requiredRoles && !protection.requiredPermissions) {
        return true;
    }

    // If authentication is required but user is not authenticated
    if (protection.requireAuth && !user) {
        return false;
    }

    // If user is not authenticated but roles or permissions are required
    if (!user && (protection.requiredRoles || protection.requiredPermissions)) {
        return false;
    }

    // If user is authenticated, check roles and permissions
    if (user) {
        // Check required roles
        if (protection.requiredRoles && protection.requiredRoles.length > 0) {
            const hasRequiredRoles = protection.requireAllRoles
                ? hasAllRoles(user.roles, protection.requiredRoles)
                : hasAnyRole(user.roles, protection.requiredRoles);

            if (!hasRequiredRoles) {
                return false;
            }
        }

        // Check required permissions
        if (protection.requiredPermissions && protection.requiredPermissions.length > 0) {
            const hasRequiredPermissions = protection.requireAllPermissions
                ? hasAllPermissions(user.permissions, protection.requiredPermissions)
                : hasAnyPermission(user.permissions, protection.requiredPermissions);

            if (!hasRequiredPermissions) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Create a permission checker for a user
 */
export function createPermissionChecker(user: RBACUser | null): PermissionChecker {
    return {
        hasPermission: (resource: Resource, action: Action) =>
            user ? hasPermission(user.permissions, resource, action) : false,

        hasRole: (role: RoleName) =>
            user ? hasRole(user.roles, role) : false,

        hasAnyRole: (roles: RoleName[]) =>
            user ? hasAnyRole(user.roles, roles) : false,

        hasAllRoles: (roles: RoleName[]) =>
            user ? hasAllRoles(user.roles, roles) : false,

        canAccess: (protection: RouteProtection) =>
            canAccessRoute(user, protection),
    };
}

/**
 * Get the highest role in hierarchy
 */
export function getHighestRole(roles: RoleName[]): RoleName | null {
    if (roles.length === 0) return null;

    return roles.reduce((highest, current) => {
        return ROLE_HIERARCHY[current] > ROLE_HIERARCHY[highest] ? current : highest;
    });
}

/**
 * Check if one role is higher than another in hierarchy
 */
export function isRoleHigher(role1: RoleName, role2: RoleName): boolean {
    return ROLE_HIERARCHY[role1] > ROLE_HIERARCHY[role2];
}

/**
 * Sort roles by hierarchy (highest first)
 */
export function sortRolesByHierarchy(roles: RoleName[]): RoleName[] {
    return [...roles].sort((a, b) => ROLE_HIERARCHY[b] - ROLE_HIERARCHY[a]);
}

/**
 * Validate permission object
 */
export function isValidPermission(permission: any): permission is Permission {
    const normalized = normalizePermissionLoose(permission);
    if (!normalized) return false;
    // Explicitly ensure resource-action combination is supported by our model
    if (normalized.resource === 'user') {
        return ['create', 'list', 'set-role', 'ban', 'impersonate', 'delete', 'set-password'].includes(normalized.action);
    }
    if (normalized.resource === 'project') {
        return ['create', 'view', 'update', 'delete'].includes(normalized.action);
    }
    return false;
}

/**
 * Sanitize and validate user roles
 */
export function sanitizeUserRoles(roles: any[]): RoleName[] {
    if (!Array.isArray(roles)) return [];

    return roles.filter((role): role is RoleName =>
        typeof role === 'string' && ['admin', 'user', 'editor'].includes(role)
    );
}

/**
 * Sanitize and validate user permissions
 */
export function sanitizeUserPermissions(permissions: any[]): Permission[] {
    if (!Array.isArray(permissions)) return [];
    const result: Permission[] = [];
    for (const p of permissions) {
        const normalized = normalizePermissionLoose(p);
        if (normalized && isValidPermission(normalized)) {
            // Ensure we keep canonical lowercased names
            result.push({ resource: normalized.resource, action: normalized.action });
        }
    }
    return result;
}